// client/src/api/core/apiFetch.ts
//
// DIPENDENZE: errors.ts (stesso package), parseJson.ts (stesso package),
//             tokenRefresh.ts (dominio auth)
//
// ARCHITETTURA:
//   - Lo store Zustand NON viene importato staticamente a top-level.
//   - Viene ricevuto come parametro opzionale `getStore` (Dependency Injection).
//   - Il default viene registrato UNA SOLA VOLTA in main.tsx via configureApiFetch().
//   - Nei test si passa direttamente il mock come terzo argomento.

import {
  isNetworkError,
  SessionExpiredError,
  NetworkError,
  PaywallError,
  AdminMfaRequiredError,
} from "./errors";
import { parseJsonSafe } from "./parseJson";
import {
  clearMaintenanceState,
  signalMaintenanceFailure,
} from "./maintenanceGuard";
import { refreshSession } from "../auth/tokenRefresh";
import { getDeviceFingerprint } from "@/utils/deviceFingerprint";
import { resolveApiBaseUrl } from "@/utils/apiBase";
import { enqueueFailedRequest } from "@/utils/offlineRequestQueue";

// Risoluzione dell'URL base: vedi `utils/apiBase.ts`.
//
// Il fallback NON può usare `??`: la CI passava `VITE_API_URL` vuota e il
// nullish non scatta su stringa vuota → API_BASE restava "" e in WebView le
// fetch finivano su `https://localhost/api/...` ("server non raggiungibile").
const API_BASE: string = resolveApiBaseUrl();

// ── Endpoint del FLUSSO DI AUTENTICAZIONE (utente NON loggato) ──────────────
//
// FIX 2FA-401 (Ott 2026): su questi path un 401 è una RISPOSTA APPLICATIVA
// normale — credenziali errate, challenge 2FA scaduta/errata, codice email
// non valido — NON una sessione scaduta. Prima, il flusso generico
// "401 → refresh → logout → SessionExpiredError" mascherava il messaggio del
// server: con la challenge 2FA scaduta l'utente vedeva "Sessione scaduta"
// (e subiva un logout spurio) invece di "Verifica scaduta. Riprova ad
// accedere.". Da qui il 401 prosegue verso la gestione errori standard e
// conserva `data.code` (es. MFA_CHALLENGE_EXPIRED) per la UI.
// Deduplica dei log di refresh fallito (29 Ott 2026): N richieste parallele
// producevano N righe identiche in console.
let _lastRefreshFailureLogAt = 0;

// ── Refresh PROATTIVO (3 Nov 2026) ──────────────────────────────────────────
// PERCHÉ: l'access token è persistito in localStorage e sopravvive al reload,
// ma dura pochi minuti. Al primo caricamento della pagina decine di componenti
// partivano con il token SCADUTO → una raffica di `401` in console (e in
// Network) prima che il refresh potesse avvenire. Ora, se il token è scaduto
// (o scade entro 30 s), si refresha UNA volta sola prima di inviare la
// richiesta: la raffica di 401 sparisce e il refresh non viene duplicato.
let _proactiveRefresh: Promise<void> | null = null;

/** true se il JWT è scaduto o sta per scadere entro `marginMs`. */
function accessTokenExpiresSoon(
  token: string | null,
  marginMs = 30_000,
): boolean {
  if (!token) return false;
  const payloadPart = token.split(".")[1];
  if (!payloadPart) return false;
  try {
    const base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64)) as { exp?: unknown };
    return (
      typeof payload.exp === "number" &&
      payload.exp * 1000 - Date.now() <= marginMs
    );
  } catch {
    return false;
  }
}

/**
 * Se il token è in scadenza, attende un refresh condiviso prima della richiesta.
 * Best effort: se il refresh fallisce si prosegue e il flusso 401/refresh
 * esistente resta l'unico responsabile del logout.
 */
async function refreshBeforeRequestIfExpired(
  store: AuthStoreSnapshot,
): Promise<void> {
  if (!accessTokenExpiresSoon(store.accessToken)) return;
  if (!_proactiveRefresh) {
    _proactiveRefresh = refreshSession(store)
      .then(() => undefined)
      .catch(() => {
        /* il flusso 401 resta il fallback */
      })
      .finally(() => {
        _proactiveRefresh = null;
      });
  }
  await _proactiveRefresh;
}

const AUTH_FLOW_401_PATHS: readonly string[] = [
  "/api/auth/login",
  "/api/auth/google",
  "/api/auth/mfa/verify-login",
  "/api/auth/register",
  "/api/auth/register-dealer",
  "/api/auth/verify-email",
  "/api/auth/resend-verification",
  "/api/auth/password-reset-request",
  "/api/auth/password-reset-confirm",
  "/api/auth/mfa/reset-request",
  "/api/auth/mfa/reset-confirm",
  // Sessione unica (Ott 2026): risoluzione conflitto "accesso già attivo".
  "/api/auth/session-conflict",
  // Sblocco biometrico: pre-login per definizione — un 401 significa
  // "device revocato/scaduto", non "sessione scaduta da rinnovare".
  "/api/auth/biometric/refresh",
  "/api/mfa/challenge",
];

/** true se il path appartiene al flusso di autenticazione (query string esclusa). */
function isAuthFlowPath(path: string): boolean {
  const base = path.split("?")[0].replace(/\/+$/, "");
  return AUTH_FLOW_401_PATHS.includes(base);
}

if (typeof window !== "undefined" && !API_BASE) {
  // Su WebView Capacitor non esiste un proxy: senza base URL le fetch
  // andrebbero verso `undefined/api/...` mandando in crash il bootstrap.
  // Logghiamo solo — è lib/api.ts a gestire il fallback runtime.
  // eslint-disable-next-line no-console
  console.warn(
    "[apiFetch] VITE_API_BASE_URL non impostata. Le chiamate API useranno URL relativi.",
  );
}

// ── Interfacce pubbliche ──────────────────────────────────────────────────────

export interface ApiErrorResponse {
  error?: string;
  message?: string;
  paywall?: boolean;
  feature?: string;
  details?: Array<{ message: string }>;
}

/**
 * Subset minimale dello store che apiFetch necessita.
 * Usare un'interfaccia ristretta (anziché l'intero store) mantiene
 * il modulo deaccoppiato e i test semplici.
 */
export interface AuthStoreSnapshot {
  accessToken: string | null;
  /** true quando lo store ha completato l'idratazione della sessione */
  isHydrated?: boolean;
  updateAccess: (accessToken: string, user?: unknown) => void;
  logout: () => void;
  /** FIX NO-LOGOUT-RECORDING (20 Set 2026): logout consapevole della
   *  registrazione in corso — se attiva RIMANDA il logout. Preferito a
   *  logout() nudo nei punti in cui la sessione sembra morta. */
  expireSession?: () => Promise<void> | void;
}

/** Firma del getter lazy dello store: `() => useAuthStore.getState()` */
export type GetAuthStore = () => AuthStoreSnapshot;

// ── Default store registry ────────────────────────────────────────────────────

let _defaultGetStore: GetAuthStore | undefined;

/**
 * Registra il getter globale dello store Zustand.
 * Va chiamato UNA SOLA VOLTA nel punto di bootstrap dell'app (main.tsx),
 * PRIMA di qualsiasi chiamata API.
 *
 * @example
 * // main.tsx
 * import { useAuthStore } from './stores/useAuthStore';
 * import { configureApiFetch } from './api/core/apiFetch';
 *
 * configureApiFetch(() => useAuthStore.getState());
 */
export function configureApiFetch(getStore: GetAuthStore): void {
  _defaultGetStore = getStore;
}

/**
 * Risolve il getter dello store: usa quello passato esplicitamente,
 * oppure il default registrato. Lancia in development se entrambi mancano.
 */
function resolveStore(getStore?: GetAuthStore): GetAuthStore {
  const resolved = getStore ?? _defaultGetStore;

  if (!resolved) {
    throw new Error(
      "[apiFetch] Store non configurato. " +
        "Chiama configureApiFetch(() => useAuthStore.getState()) in main.tsx " +
        "prima di effettuare chiamate API.",
    );
  }

  return resolved;
}

// ── Retry configuration ──────────────────────────────────────────────────────
//
// FASE 2 (Luglio 2026): Exponential backoff per errori transitori.
// - 429 Too Many Requests → backoff con jitter
// - 502 Bad Gateway → retry (Render cold start)
// - 503 Service Unavailable → retry (manutenzione/overload)
// - Retry solo su metodi idempotenti (GET, HEAD, OPTIONS) o se il server
//   restituisce l'header Retry-After.
//
// Comportamento:
//   Tentativo 1 → immediato
//   Tentativo 2 → dopo ~1s (±25% jitter)
//   Tentativo 3 → dopo ~2s (±25% jitter)
//   Tentativo 4 → dopo ~4s (±25% jitter)
//   Totale max: 4 tentativi (~7 secondi)

// 429 NON è retryable: "Troppe richieste" significa "rallenta", riprovare peggiora la situazione.
// Solo 502 (Bad Gateway, cold start Render) e 503 (manutenzione) sono retryable.
const RETRYABLE_STATUSES = new Set([502, 503]);
const MAX_RETRIES = 3; // 3 retry dopo il primo tentativo = 4 totali
const BASE_DELAY_MS = 1000;
const IDEMPOTENT_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

function jitter(delay: number): number {
  return delay * (0.75 + Math.random() * 0.5); // ±25%
}

function isRetryable(
  method: string | undefined,
  status: number,
  headers: Headers,
): boolean {
  if (!RETRYABLE_STATUSES.has(status)) return false;
  // Retry sempre se il metodo è idempotente
  if (IDEMPOTENT_METHODS.has((method ?? "GET").toUpperCase())) return true;
  // Per metodi non idempotenti (POST, PUT, DELETE), retry solo se il server
  // lo indica esplicitamente con Retry-After o se è un 503 (probabile cold start
  // Render, la richiesta non è stata processata)
  if (status === 503) return true;
  return headers.has("Retry-After");
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Restituisce true solo per metodi HTTP che ammettono un body. */
function methodHasBody(method: string | undefined): boolean {
  return ["POST", "PUT", "PATCH"].includes((method ?? "GET").toUpperCase());
}

// ── Maintenance events ────────────────────────────────────────────────────────
//
// Quando il backend risponde 503 (es. quota compute Neon esaurita →
// {code: "DATABASE_UNAVAILABLE"}) la UI deve mostrare una schermata di
// manutenzione invece di layout rotti o spinner infiniti.
// La decisione (quante segnalazioni, con che finestra, con che grazia) vive in
// `maintenanceGuard.ts`; qui segnaliamo una sola volta per richiesta.

// ── Core fetch ────────────────────────────────────────────────────────────────

/**
 * Wrapper tipizzato attorno a `fetch` con:
 * - Authorization header automatico (Bearer token dallo store)
 * - Retry trasparente su 401 con refresh del token (una sola volta)
 * - Rilevamento e rilancio di AbortError, NetworkError, PaywallError,
 *   SessionExpiredError
 *
 * @param path     Percorso API relativo, es. `/api/routes`
 * @param init     RequestInit standard; supporta `signal` per AbortController.
 *                 Default: `{}`
 * @param getStore Getter lazy dello store. Omettere in produzione (usa il
 *                 default di `configureApiFetch`); passare esplicitamente nei test.
 * @param _retry   Uso interno: `false` durante il retry post-refresh per
 *                 prevenire loop. Non passare dall'esterno. Default: `true`
 *
 * @throws {NetworkError}        Su errori di rete o risposta HTTP non-ok
 * @throws {PaywallError}        Su 403 con `paywall: true` nel body
 * @throws {SessionExpiredError} Su 401 non recuperabile con refresh
 * @throws {DOMException}        Se la richiesta viene abortita (AbortError)
 */
/**
 * Opzioni extra accettate da {@link apiFetch} oltre a `RequestInit`.
 *
 * Servono alle chiamate LENTE e non idempotenti (es. generazione percorso AI,
 * 8-30 s): con i default un cold start del backend supera i 45 s, il tentativo
 * va in timeout e i retry automatici tengono l'utente davanti a uno spinner per
 * minuti ("girava all'infinito senza risultato" — segnalazione utente, Ott 2026),
 * ripetendo lato server una generazione costosa.
 */
export interface ApiFetchExtras {
  /** Timeout per singolo tentativo (ms). Default: 45 s (60 s per gli upload). */
  timeoutMs?: number;
  /** Retry extra dopo il primo tentativo. Default: 3. */
  maxRetries?: number;
}

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit & ApiFetchExtras = {},
  getStore?: GetAuthStore,
  _retry = true,
): Promise<T> {
  const {
    timeoutMs: timeoutOverride,
    maxRetries: maxRetriesOverride,
    ...requestInit
  } = init;
  const resolvedGetStore = resolveStore(getStore);
  const storeSnapshot = resolvedGetStore();
  let accessToken = storeSnapshot.accessToken;

  // Refresh proattivo: evita la raffica di 401 quando il token è scaduto
  // (reload della pagina con token persistito e ormai vecchio). Il getter
  // dello store viene richiamato SOLO se c'è davvero un refresh da fare,
  // così il numero di letture resta invariato nel caso normale.
  if (_retry && !isAuthFlowPath(path) && accessTokenExpiresSoon(accessToken)) {
    await refreshBeforeRequestIfExpired(storeSnapshot);
    accessToken = resolvedGetStore().accessToken;
  }

  // Costruiamo sempre un nuovo oggetto Headers per non mutare init.headers
  const headers = new Headers(init.headers);

  // Content-Type: application/json solo se la richiesta ha un body
  // E il body NON è una FormData / Blob / URLSearchParams.
  //
  // Non va iniettato su GET/DELETE/HEAD: alcuni server interpretano
  // la sua presenza come segnale di body atteso e rifiutano la richiesta.
  //
  // FIX #FORMDATA-CT: Se il body è FormData, il browser deve impostare
  // automaticamente `multipart/form-data; boundary=...`. Forzare
  // `application/json` rompe l'upload multipart (es. foto moto) perché
  // il server non riesce a parsare il body come JSON e risponde 400.
  const body = init.body;
  const isMultipartBody =
    (typeof FormData !== "undefined" && body instanceof FormData) ||
    (typeof Blob !== "undefined" && body instanceof Blob) ||
    (typeof URLSearchParams !== "undefined" &&
      body instanceof URLSearchParams) ||
    (typeof ReadableStream !== "undefined" && body instanceof ReadableStream);

  if (
    methodHasBody(init.method) &&
    !headers.has("Content-Type") &&
    !isMultipartBody
  ) {
    headers.set("Content-Type", "application/json");
  }

  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  } else {
    // Rimuoviamo esplicitamente per non propagare token scaduti
    // che potrebbero essere stati passati dentro init.headers
    headers.delete("Authorization");
  }

  // FASE 2: Session binding — invia device fingerprint su ogni richiesta
  try {
    headers.set("X-Device-Fingerprint", getDeviceFingerprint());
  } catch {
    /* fingerprint non disponibile */
  }

  // ── Timeout: previene richieste appese su rete mobile lenta ──────────────
  // Il timeout viene applicato per-ogni-tentativo nel retry loop sottostante.
  // Cold-start del backend Render free tier può impiegare 30-60s.
  // Chiamate di upload (FormData, Blob) hanno timeout di 60 secondi.
  const existingSignal = init.signal;
  const timeoutMs = timeoutOverride ?? (isMultipartBody ? 60_000 : 45_000);
  const maxRetries = maxRetriesOverride ?? MAX_RETRIES;

  // ── Network call with retry ──────────────────────────────────────────────
  // FASE 2: Exponential backoff per 429/502/503 su metodi idempotenti.
  let res: Response;
  let lastError: unknown = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Timeout controller per questo tentativo
    const attemptTimeout = new AbortController();
    const attemptTimeoutId = setTimeout(
      () =>
        attemptTimeout.abort(
          new DOMException(
            `Request timeout after ${timeoutMs / 1000}s`,
            "TimeoutError",
          ),
        ),
      timeoutMs,
    );

    // Combina signal esterno + timeout + eventuale abort da retry precedente
    const signals: AbortSignal[] = [attemptTimeout.signal];
    if (existingSignal) signals.push(existingSignal);
    const anyAbort = new AbortController();
    const onAbort = () => {
      const reason = signals.find((s) => s.aborted)?.reason;
      anyAbort.abort(reason);
    };
    for (const s of signals) {
      if (s.aborted) {
        onAbort();
        break;
      }
      s.addEventListener("abort", onAbort, { once: true });
    }

    try {
      res = await fetch(`${API_BASE}${path}`, {
        ...init,
        headers,
        credentials: "include",
        signal: anyAbort.signal,
      });
      clearTimeout(attemptTimeoutId);

      // Pulisci i signal listener per evitare memory leak
      for (const s of signals) s.removeEventListener("abort", onAbort);

      // Se la risposta NON è retryable, esci dal loop
      if (!isRetryable(init.method, res.status, res.headers)) break;

      // NB: qui NON si segnala la manutenzione. Il 503 viene contato una sola
      // volta per richiesta, dopo i retry (vedi sotto): contarlo a ogni
      // tentativo faceva comparire l'overlay per un singolo hiccup risolto dal
      // retry successivo («clicco Notizie, dice manutenzione, poi parte»).

      // Se è l'ultimo tentativo, non ritentare
      if (attempt >= maxRetries) break;

      // Calcola delay con backoff esponenziale + jitter
      const delay = jitter(BASE_DELAY_MS * Math.pow(2, attempt));
      if (typeof window !== "undefined") {
        console.warn(
          `[apiFetch] ${res.status} on ${init.method ?? "GET"} ${path} — ` +
            `retry ${attempt + 1}/${maxRetries} in ${Math.round(delay)}ms`,
        );
      }
      await new Promise((r) => setTimeout(r, delay));
      continue;
    } catch (networkErr) {
      clearTimeout(attemptTimeoutId);
      for (const s of signals) s.removeEventListener("abort", onAbort);

      lastError = networkErr;

      // AbortError da AbortController esterno: non ritentare
      if (
        networkErr instanceof DOMException &&
        networkErr.name === "AbortError"
      ) {
        throw networkErr;
      }

      // Timeout: ritenta se non è l'ultimo tentativo
      if (
        networkErr instanceof DOMException &&
        networkErr.name === "TimeoutError"
      ) {
        if (attempt < maxRetries) {
          const delay = jitter(BASE_DELAY_MS * Math.pow(2, attempt));
          if (typeof window !== "undefined") {
            console.warn(
              `[apiFetch] Timeout on ${path} — retry ${attempt + 1}/${maxRetries} in ${Math.round(delay)}ms`,
            );
          }
          await new Promise((r) => setTimeout(r, delay));
          continue;
        }
        // Ultimo tentativo: propaga come NetworkError
        throw new NetworkError(
          `Request timeout after multiple retries (${path})`,
          0,
        );
      }

      // Network error generico: ritenta se possibile
      if (attempt < maxRetries && isNetworkError(networkErr)) {
        const delay = jitter(BASE_DELAY_MS * Math.pow(2, attempt));
        if (typeof window !== "undefined") {
          console.warn(
            `[apiFetch] Network error on ${path} — retry ${attempt + 1}/${maxRetries} in ${Math.round(delay)}ms`,
          );
        }
        await new Promise((r) => setTimeout(r, delay));
        continue;
      }

      // Errore non retryable: esci
      break;
    }
  }

  // Se dopo tutti i retry non abbiamo una risposta, propaga l'errore
  if (!res!) {
    // Log dettagliato per debug su dispositivi fisici
    if (typeof window !== "undefined") {
      console.error("[apiFetch] All retries exhausted:", {
        url: `${API_BASE}${path}`,
        method: init.method ?? "GET",
        apiBase: API_BASE,
        errorName: (lastError as { constructor?: { name?: string } } | null)
          ?.constructor?.name,
        errorMessage:
          lastError instanceof Error ? lastError.message : String(lastError),
        isOnline: navigator.onLine,
        timestamp: new Date().toISOString(),
      });
    }

    const msg = isNetworkError(lastError)
      ? `Server non raggiungibile (${API_BASE || "relative URL"}). Verifica connessione e che il server sia online.`
      : lastError instanceof Error
        ? lastError.message
        : "Errore di rete sconosciuto.";

    // OFFLINE (Ott 2026): una scrittura persa per assenza di rete viene messa
    // in coda e rigiocata al ritorno della connessione. Best effort: se
    // l'accodamento fallisce (IndexedDB indisponibile) resta l'errore.
    try {
      await enqueueFailedRequest(path, init);
    } catch {
      /* l'errore di rete originale è più utile di un errore di coda */
    }

    throw new NetworkError(msg, 0);
  }

  // ── 401: tentativo unico di refresh token ─────────────────────────────────
  // FIX 2FA-401: gli endpoint del flusso di autenticazione sono esclusi — un
  // loro 401 va propagato con il messaggio del server (vedi AUTH_FLOW_401_PATHS).
  if (res.status === 401 && _retry && !isAuthFlowPath(path)) {
    // SESSIONE-UNICA (Ott 2026): se il server dichiara la sessione SOSTITUITA
    // da un nuovo accesso (stesso account su un altro device/canale), il
    // refresh token è già stato revocato: tentare il refresh sarebbe inutile e
    // ritarderebbe solo il logout. Si slogga SUBITO con un messaggio chiaro.
    let revokedCode: string | undefined;
    try {
      const peek = await res.clone().json();
      revokedCode = (peek as { code?: string } | null)?.code;
    } catch {
      /* corpo non JSON: si prosegue col flusso normale */
    }
    if (revokedCode === "SESSION_REVOKED") {
      const currentStore = resolvedGetStore();
      if (currentStore.expireSession) {
        currentStore.expireSession();
      } else {
        currentStore.logout();
      }
      throw new SessionExpiredError(
        "Sessione conclusa: è stato effettuato un accesso con lo stesso account su un altro dispositivo.",
      );
    }

    // Nessun access token in memoria E store già idratato → sessione morta.
    // Evita di chiamare POST /api/auth/refresh per ogni 401 (spam in console
    // e rate-limit) e forza un logout pulito. Se lo store non è ancora
    // idratato (avvio app nativa che ripristina la sessione da Capacitor
    // Preferences) lasciamo che il refresh tenti: il token può esistere lato
    // server/cookie anche se non è ancora in memoria.
    const currentStore = resolvedGetStore();
    if (!currentStore.accessToken && currentStore.isHydrated !== false) {
      // FIX NO-LOGOUT-RECORDING (20 Set 2026): expireSession NON fa logout
      // se una registrazione è attiva (perderebbe il percorso in corso).
      if (currentStore.expireSession) {
        currentStore.expireSession();
      } else {
        currentStore.logout();
      }
      throw new SessionExpiredError("Sessione scaduta");
    }

    // Log diagnostico: aiuta a distinguere 401 da token scaduto vs cookie mancante
    if (typeof window !== "undefined") {
      console.warn("[apiFetch] 401 ricevuta, avvio refresh token...", {
        path,
        method: init.method ?? "GET",
        hasAccessToken: !!accessToken,
        timestamp: new Date().toISOString(),
      });
    }

    try {
      await refreshSession(resolvedGetStore());

      return apiFetch<T>(path, init, resolvedGetStore, false);
    } catch (refreshErr) {
      // Log diagnostico: il refresh è fallito. Deduplica: con N richieste
      // parallele il fallimento è lo stesso, ma la console ne mostrava N.
      // Il messaggio del server (es. "Refresh token mancante") è la chiave
      // per capire se è scaduto o se il cookie non è stato inviato.
      const now = Date.now();
      if (
        typeof window !== "undefined" &&
        now - _lastRefreshFailureLogAt > 5_000
      ) {
        _lastRefreshFailureLogAt = now;
        console.error(
          `[apiFetch] Refresh token fallito: ${
            refreshErr instanceof Error
              ? refreshErr.message
              : String(refreshErr)
          }`,
          {
            path,
            method: init.method ?? "GET",
            isSessionExpired: refreshErr instanceof SessionExpiredError,
            timestamp: new Date().toISOString(),
          },
        );
      }

      if (
        isNetworkError(refreshErr) ||
        !(refreshErr instanceof SessionExpiredError)
      ) {
        // Caso race condition: un'altra tab ha già aggiornato il token
        const latestSnapshot = resolvedGetStore();

        if (
          latestSnapshot.accessToken !== null &&
          latestSnapshot.accessToken !== accessToken
        ) {
          return apiFetch<T>(path, init, resolvedGetStore, false);
        }

        const msg =
          refreshErr instanceof Error
            ? refreshErr.message
            : "Server non raggiungibile.";

        throw new NetworkError(msg, 0);
      }

      // SessionExpiredError: propagiamo verso l'UI per triggerare il logout
      throw refreshErr;
    }
  }

  // ── Parsing del body ──────────────────────────────────────────────────────
  const data: unknown = await parseJsonSafe(res);

  // ── Stato manutenzione globale ────────────────────────────────────────────
  // 503 = backend vivo ma DB/risorse non disponibili (Neon quota, cold start
  // mai completato, overload). Una sola segnalazione per richiesta, dopo i
  // retry: l'overlay si monta solo se più richieste distinte falliscono e il
  // servizio non torna nei MAINTENANCE_GRACE_MS successivi.
  // Qualsiasi 2xx significa "tutto ok" e azzera lo stato automaticamente.
  if (res.ok) {
    clearMaintenanceState();
  } else if (res.status === 503) {
    signalMaintenanceFailure();
  }

  // ── 402/403: Paywall o MFA Required ──────────────────────────────────────
  //
  // FIX PAYWALL-402 (Ott 2026): i paywall DEALER (checkDealerFeature /
  // checkDealerLimits) rispondono 402 Payment Required con `paywall: true`
  // (contratto dichiarato nel server: "fa scattare il PaywallError lato
  // client"), mentre qui si intercettava SOLO il 403 dei paywall rider: tutte
  // le superfici che discriminano su `e.name === 'PaywallError'` mostravano un
  // errore generico invece del modale di upgrade. Ora anche il 402 diventa
  // PaywallError (con `status` valorizzato, per non rompere i check
  // legacy `e.status === 402`).
  if (res.status === 402 || res.status === 403) {
    const errorBody = data as ApiErrorResponse & {
      code?: string;
      setupUrl?: string;
    };

    // MFA richiesto per pannello admin (solo 403)
    if (res.status === 403 && errorBody.code === "MFA_REQUIRED") {
      // FIX MFA-UI (Ott 2026): l'errore era gestito SOLO da Admin.tsx e
      // AdminDashboardPage: tutte le altre sezioni del pannello (Notizie,
      // Broadcast, Feature Flags, Moderazione, Ops, Monitoraggio, Audit,
      // Impostazioni, Strumenti, Analytics, RBAC, Financials, Pagamenti…)
      // mostravano un errore generico senza spiegare cosa fare. Ora l'evento
      // raggiunge AdminLayout, che mostra l'avviso con il link al setup MFA.
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("rc:admin-mfa-required", {
            detail: { setupUrl: errorBody.setupUrl ?? "/admin/security" },
          }),
        );
      }
      throw new AdminMfaRequiredError(
        errorBody.error ?? "MFA richiesto per accedere al pannello admin.",
        errorBody.setupUrl ?? "/admin/security",
      );
    }

    if (errorBody.paywall === true) {
      throw new PaywallError(
        errorBody.error ?? "Questa funzionalità richiede Rider Plus.",
        errorBody.feature,
        res.status,
      );
    }
  }

  // ── Qualsiasi altro errore HTTP ───────────────────────────────────────────
  if (!res.ok) {
    const errorBody = data as ApiErrorResponse;

    const detailMsg =
      Array.isArray(errorBody.details) && errorBody.details.length > 0
        ? errorBody.details[0]?.message?.trim()
        : undefined;

    const msg =
      detailMsg ||
      errorBody.message ||
      errorBody.error ||
      `Request failed with status ${res.status}`;

    // Creiamo l'errore e alleghiamo il body originale come proprietà
    // aggiuntiva — utile per debug nei catch upstream senza cast unsafe.
    const httpError = Object.assign(new NetworkError(msg, res.status), {
      data,
    });

    throw httpError;
  }

  return data as T;
}
